import type { User as AppUser, RoleInfo } from '../../../../types/user';
import type { EpcProjectTeamRole } from '../../../../services/epcProjectService';

const TARGET_DEPARTMENT = 'EPC' as const;

/**
 * Maps an EPC project team role to the User-Management role names that should
 * be allowed in that slot. Matching is case-insensitive against the role's
 * `name`. Multiple aliases let admins use either the canonical name (e.g.
 * "Site Engineer") or shorter equivalents.
 *
 * Tag-based matching (via `__tag__` sentinels in `epcRoleNamesForUser`) takes
 * priority and makes name aliases a secondary fallback — admins can name their
 * EPC roles anything as long as the role's `tag` field is set correctly.
 */
const EPC_ROLE_NAME_ALIASES: Record<EpcProjectTeamRole, string[]> = {
  epc_head: ['epc head', 'head'],
  pmo: ['pmo', 'pmo head', 'project management officer'],
  project_head: ['project head'],
  project_coordinator: ['project coordinator', 'coordinator'],
  department_head: ['department head', 'dept head', 'head'],
  manager: ['manager'],
  site_engineer: ['site engineer', 'engineer'],
};

function normalize(s: string | undefined | null): string {
  return (s ?? '').trim().toLowerCase();
}

/**
 * Returns every User-Management role this user holds inside the EPC department,
 * combining the per-department roles[] array and the legacy single-role field.
 * Each entry is either the Role's display name (normalized to lowercase) or a
 * `__tag__<value>` sentinel when the role doc exposes an EPC tier tag — the
 * tag sentinel lets `userMatchesEpcRole` match regardless of the admin-chosen
 * role name (e.g. "EPC Manager" still matches the `manager` slot).
 */
function epcRoleNamesForUser(user: AppUser): string[] {
  const out: string[] = [];

  if (Array.isArray(user.roles)) {
    for (const entry of user.roles) {
      if (entry?.department !== TARGET_DEPARTMENT) continue;
      const role = entry.roleId;
      if (role && typeof role === 'object') {
        if ('name' in role) out.push(normalize((role as RoleInfo).name));
        const tag = (role as RoleInfo).tag;
        if (tag) out.push(`__tag__${tag}`);
      }
    }
  }

  // Legacy fallback: if the user only has a single populated role and it lives
  // in the EPC department, include it too.
  if (user.role && typeof user.role === 'object') {
    const r = user.role as RoleInfo;
    if (r.department === TARGET_DEPARTMENT) {
      if (r.name) out.push(normalize(r.name));
      if (r.tag) out.push(`__tag__${r.tag}`);
    }
  }

  return Array.from(new Set(out));
}

/**
 * Whether the user holds at least one role in the EPC department that is a
 * valid match for the given EPC team role.
 * Matches by `tag` first (reliable, set by admin on the Role doc), then falls
 * back to name-alias matching for legacy roles without a tag.
 */
export function userMatchesEpcRole(user: AppUser, epcRole: EpcProjectTeamRole): boolean {
  const aliases = EPC_ROLE_NAME_ALIASES[epcRole];
  if (!aliases || aliases.length === 0) return false;
  const userRoleNames = epcRoleNamesForUser(user);
  return userRoleNames.some(
    (name) => name === `__tag__${epcRole}` || aliases.includes(name),
  );
}

/**
 * Picks the most specific matching User-Management role name for display next
 * to a candidate (e.g. "Site Engineer"). Falls back to any EPC role name on
 * the user when no alias matches, and to undefined if none.
 */
export function epcRoleLabelForUser(
  user: AppUser,
  epcRole?: EpcProjectTeamRole,
): string | undefined {
  const namesNormalized = epcRoleNamesForUser(user);
  // Strip tag sentinels before checking emptiness — a user with only a tag
  // sentinel (name not yet known) should still produce a label via the loop.
  const hasAny = namesNormalized.some((n) => !n.startsWith('__tag__'));
  if (!hasAny && namesNormalized.length === 0) return undefined;

  const allRoles = [
    ...(Array.isArray(user.roles) ? user.roles : []),
    ...(user.role && typeof user.role === 'object' ? [{ roleId: user.role, department: TARGET_DEPARTMENT }] : []),
  ];

  const findOriginal = (predicate: (name: string) => boolean): string | undefined => {
    for (const entry of allRoles) {
      const r = (entry as { roleId?: unknown }).roleId;
      if (r && typeof r === 'object' && 'name' in r) {
        const name = (r as RoleInfo).name;
        if (name && predicate(normalize(name))) return name;
      }
    }
    return undefined;
  };

  if (epcRole) {
    const aliases = EPC_ROLE_NAME_ALIASES[epcRole] ?? [];
    const matched = findOriginal((n) => aliases.includes(n));
    if (matched) return matched;
    // Fall back to any role that has the matching tag
    for (const entry of allRoles) {
      const r = (entry as { roleId?: unknown }).roleId;
      if (r && typeof r === 'object' && (r as RoleInfo).tag === epcRole) {
        return (r as RoleInfo).name;
      }
    }
  }
  return findOriginal(() => true);
}

/**
 * Filters a directory to users that match the given EPC team role within the
 * EPC department, returning each candidate paired with the role label to show
 * in the picker.
 */
export function filterDirectoryForEpcRole(
  directory: AppUser[],
  epcRole: EpcProjectTeamRole,
): Array<{ user: AppUser; epcRoleLabel: string | undefined }> {
  return directory
    .filter((u) => userMatchesEpcRole(u, epcRole))
    .map((u) => ({ user: u, epcRoleLabel: epcRoleLabelForUser(u, epcRole) }));
}
